import React, { useState } from 'react';
import './AddBusinessForm.css';
import { uploadImages, validateImageFile, createImagePreview } from '../services/fileUploadService';

function AddBusinessForm({ onSubmit, onCancel, categories }) {
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    address: '',
    phone: '',
    hours: '',
    category: '',
    photos: [],
    website: '',
    email: '',
    latitude: '',
    longitude: '',
    description: ''
  });

  const [errors, setErrors] = useState({});
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length === 0) return;

    // Validate files
    const validationErrors = [];
    const validFiles = [];

    for (const file of files) {
      const validation = validateImageFile(file);
      if (!validation.valid) {
        validationErrors.push(`${file.name}: ${validation.errors.join(', ')}`);
      } else {
        validFiles.push(file);
      }
    }

    // Check total files count
    if (photoPreviews.length + validFiles.length > 5) {
      validationErrors.push(`Maximum 5 photos allowed. Currently have ${photoPreviews.length}`);
    }

    if (validationErrors.length > 0) {
      setErrors(prev => ({
        ...prev,
        photos: validationErrors.join('\n')
      }));
      return;
    }

    // Create previews and store files
    try {
      setIsUploading(true);
      const newPreviews = [];
      
      for (let i = 0; i < validFiles.length; i++) {
        const preview = await createImagePreview(validFiles[i]);
        newPreviews.push({
          preview: preview,
          name: validFiles[i].name
        });
        setUploadProgress(Math.round((i + 1) / validFiles.length * 100));
      }

      setSelectedFiles(prev => [...prev, ...validFiles].slice(0, 5));
      setPhotoPreviews(prev => [...prev, ...newPreviews].slice(0, 5));
      
      // Clear the file input
      e.target.value = '';
      setIsUploading(false);
      setUploadProgress(0);

      // Clear any photo errors
      if (errors.photos) {
        setErrors(prev => ({
          ...prev,
          photos: ''
        }));
      }
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        photos: `Error processing images: ${error.message}`
      }));
      setIsUploading(false);
    }
  };

  const removePhoto = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const newErrors = {};
    const selectedPhotoCount = selectedFiles.length || formData.photos.length;

    if (!formData.name.trim()) newErrors.name = 'Business name is required';
    if (!formData.type.trim()) newErrors.type = 'Business type is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.hours.trim()) newErrors.hours = 'Hours are required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (selectedPhotoCount === 0) newErrors.photos = 'At least one photo is required';
    if (!/^[0-9+\-\s]+$/.test(formData.phone)) newErrors.phone = 'Invalid phone number';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setIsUploading(true);
      let uploadedPhotos = [];

      // Upload images if any
      if (selectedFiles.length > 0) {
        try {
          uploadedPhotos = await uploadImages(selectedFiles);
          console.log('Uploaded photos:', uploadedPhotos);
        } catch (uploadError) {
          setErrors(prev => ({
            ...prev,
            photos: `Failed to upload images: ${uploadError.message}`
          }));
          setIsUploading(false);
          return;
        }
      }

      // Prepare form data with uploaded photo URLs
      const finalFormData = {
        ...formData,
        photos: uploadedPhotos.length > 0 ? uploadedPhotos : formData.photos
      };

      // Call onSubmit with final data
      await onSubmit(finalFormData);
      
      setIsUploading(false);
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors(prev => ({
        ...prev,
        submit: `Error submitting form: ${error.message}`
      }));
      setIsUploading(false);
    }
  };

  return (
    <div className="add-business-overlay">
      <div className="add-business-modal">
        <div className="modal-header">
          <h2>Add Your Business</h2>
          <button className="close-button" onClick={onCancel}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="add-business-form">
          {/* Business Name */}
          <div className="form-group">
            <label htmlFor="name">Business Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter your business name"
              maxLength="100"
            />
            {errors.name && <span className="error">{errors.name}</span>}
          </div>

          {/* Category */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">Category *</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
              >
                <option value="">Select a category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
              {errors.category && <span className="error">{errors.category}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="type">Business Type *</label>
              <input
                type="text"
                id="type"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                placeholder="e.g., Retail, Service, etc."
                maxLength="50"
              />
              {errors.type && <span className="error">{errors.type}</span>}
            </div>
          </div>

          {/* Address */}
          <div className="form-group">
            <label htmlFor="address">Address *</label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="Enter complete address"
              rows="3"
              maxLength="500"
            />
            {errors.address && <span className="error">{errors.address}</span>}
          </div>

          {/* Description */}
          <div className="form-group">
            <label htmlFor="description">Description (Optional)</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Tell about your business..."
              rows="3"
              maxLength="500"
            />
          </div>

          {/* Contact Details */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="phone">Phone *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+91 XXXXX XXXXX"
                maxLength="20"
              />
              {errors.phone && <span className="error">{errors.phone}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email">Email (Optional)</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="your@email.com"
              />
            </div>
          </div>

          {/* Hours and Website */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="hours">Operating Hours *</label>
              <input
                type="text"
                id="hours"
                name="hours"
                value={formData.hours}
                onChange={handleInputChange}
                placeholder="e.g., 9:00 AM - 9:00 PM"
                maxLength="50"
              />
              {errors.hours && <span className="error">{errors.hours}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="website">Website (Optional)</label>
              <input
                type="url"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="https://example.com"
              />
            </div>
          </div>

          {/* Location (Latitude/Longitude) */}
          <div className="form-group">
            <label>Location Coordinates (Optional)</label>
            <div className="form-row">
              <input
                type="number"
                name="latitude"
                value={formData.latitude}
                onChange={handleInputChange}
                placeholder="Latitude"
                step="0.0001"
              />
              <input
                type="number"
                name="longitude"
                value={formData.longitude}
                onChange={handleInputChange}
                placeholder="Longitude"
                step="0.0001"
              />
            </div>
            <small>Get coordinates from Google Maps</small>
          </div>

          {/* Photo Upload */}
          <div className="form-group">
            <label htmlFor="photos">Upload Photos of Your Business * (Max 5)</label>
            <div className="photo-upload-area">
              <input
                type="file"
                id="photos"
                multiple
                accept="image/*"
                onChange={handlePhotoUpload}
                style={{ display: 'none' }}
              />
              <label htmlFor="photos" className="upload-button">
                📸 Click to Upload Photos
              </label>
              <small>JPG, PNG - Max 5 photos, up to 5MB each</small>
            </div>
            {errors.photos && <span className="error">{errors.photos}</span>}

            {/* Upload Progress */}
            {isUploading && uploadProgress > 0 && (
              <div className="upload-progress">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${uploadProgress}%` }}>
                    {uploadProgress}%
                  </div>
                </div>
              </div>
            )}

            {/* Photo Previews */}
            {photoPreviews.length > 0 && (
              <div className="photo-previews">
                {photoPreviews.map((item, index) => (
                  <div key={index} className="photo-preview">
                    <img 
                      src={typeof item === 'string' ? item : item.preview} 
                      alt={`Preview ${index + 1}`} 
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="remove-photo"
                      disabled={isUploading}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="photo-count">
              {photoPreviews.length} of 5 photos selected
            </div>
          </div>

          {/* Error Message */}
          {errors.submit && (
            <div className="error-message">
              {errors.submit}
            </div>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            <button 
              type="submit" 
              className="submit-button"
              disabled={isUploading}
            >
              {isUploading ? '⏳ Uploading...' : '✓ Add My Business'}
            </button>
            <button 
              type="button" 
              onClick={onCancel} 
              className="cancel-button"
              disabled={isUploading}
            >
              Cancel
            </button>
          </div>

          <p className="form-note">
            * Required fields. Your business will be reviewed before appearing in the directory.
          </p>
        </form>
      </div>
    </div>
  );
}

export default AddBusinessForm;
